/**
 * The SweetAlert shaped front door onto our own dialog.
 *
 * 87 calls in 15 files use `Swal.fire`, six of them inline in EJS pages that
 * cannot import anything. Rewriting all of them in one go to drop a plugin
 * would be a change nobody could review, so this speaks the same language: the
 * options those calls already pass, and a result object with the same shape.
 * The dialog underneath is ours (see dialog.js), and call sites can move to it
 * one at a time, or never.
 *
 * Only what the call sites actually use is implemented. The full plugin has
 * three times this surface, and an option nobody passes is an option nobody
 * needs to maintain. Unknown options are ignored rather than refused, so a
 * forgotten one degrades into a plainer dialog instead of a broken page.
 *
 * Two behaviours are worth knowing because they are subtle and both appear in
 * this codebase:
 *
 *   preConfirm returning undefined means "take the input value", not "take
 *   nothing". Several call sites validate and fall off the end of the function.
 *
 *   A validation message put up during preConfirm keeps the dialog open, even
 *   when the handler returns nothing. Half the call sites say `return false`
 *   and the other half rely on the message alone.
 */
const { openDialog } = require('./dialog');
const { toast } = require('./toast');
const { FIELD, LABEL, el, chip } = require('./style');

const INPUT_TYPES = ['text', 'email', 'password', 'number', 'url', 'tel', 'search'];

// The dialog on screen, for the statics that reach for it from outside.
let current = null;

/**
 * Whatever was passed as `html`, as something that can be appended.
 *
 * Three shapes turn up, and the plugin took all three: a string of markup, a
 * DOM node, and a jQuery object. The folder dialog in dashboard.js builds its
 * fields in jQuery and hands over the element, which a version of this that
 * only did strings rendered as the text "[object HTMLDivElement]".
 *
 * @param {string|Node|Object} value
 * @returns {Node}
 */
function htmlNode(value) {
    if (value && value.nodeType) return value;
    // A jQuery object, or anything else array-like holding a node.
    if (value && typeof value === 'object' && value[0] && value[0].nodeType) return value[0];

    const wrap = el('div', '');
    wrap.innerHTML = String(value); // our own markup, never user input
    return wrap;
}

/**
 * The input, when one was asked for.
 * @returns {HTMLElement|null}
 */
function inputNode(options) {
    if (!options.input) return null;

    const wrap = el('div', 'mt-4 text-left');
    if (options.inputLabel) wrap.appendChild(el('label', LABEL, options.inputLabel));

    const isArea = options.input === 'textarea';
    const field = el(isArea ? 'textarea' : 'input', FIELD);
    if (isArea) field.rows = 4;
    else field.type = INPUT_TYPES.indexOf(options.input) === -1 ? 'text' : options.input;

    if (options.inputPlaceholder) field.placeholder = options.inputPlaceholder;
    if (options.inputValue !== undefined && options.inputValue !== null) field.value = String(options.inputValue);
    if (options.inputAttributes && typeof options.inputAttributes === 'object') {
        Object.keys(options.inputAttributes).forEach(function (name) {
            field.setAttribute(name, options.inputAttributes[name]);
        });
    }

    // Enter in a single line field means confirm, the way it does in a form.
    if (!isArea) {
        field.addEventListener('keydown', function (event) {
            if (event.key !== 'Enter' || !current) return;
            event.preventDefault();
            const confirm = current.button('confirm');
            if (confirm && !confirm.disabled) confirm.click();
        });
    }

    wrap.appendChild(field);
    wrap.__field = field;
    return wrap;
}

/**
 * The buttons, left to right.
 *
 * The plugin put the confirm button first, on the left. Here the way out is on
 * the left and the thing you came to do is on the right, which is where every
 * other desktop dialog puts it and where the mouse already is after reading.
 *
 * @returns {Array}
 */
function buttonsFor(options) {
    const buttons = [];
    if (options.showCancelButton) {
        buttons.push({ id: 'cancel', label: options.cancelButtonText || 'Cancel', tone: 'neutral' });
    }
    if (options.showDenyButton) {
        buttons.push({ id: 'deny', label: options.denyButtonText || 'No', tone: 'danger' });
    }
    if (options.showConfirmButton !== false) {
        buttons.push({
            id: 'confirm',
            label: options.confirmButtonText || 'OK',
            tone: isDestructive(options.confirmButtonColor) ? 'danger' : 'primary',
            // A destructive default should be reached for, not landed on.
            autofocus: options.focusConfirm !== false && !options.input
                && !isDestructive(options.confirmButtonColor)
        });
    }
    return buttons;
}

/**
 * Whether this is a message that dismisses itself rather than a question.
 * @returns {boolean}
 */
function selfClosing(config) {
    return Boolean(config.timer) && config.showConfirmButton === false && !config.input && !config.html;
}

/**
 * One line out of a title and a text that were written for two.
 * @returns {string}
 */
function toastMessage(config) {
    const title = (config.title || '').trim();
    const text = (config.text || '').trim();
    if (!title) return text;
    if (!text) return title;
    return /[.!?:]$/.test(title) ? title + ' ' + text : title + ': ' + text;
}

/**
 * Whether a colour was asking for the destructive button.
 *
 * Call sites pass '#d33' and '#dc2626' for the same intent, neither of which is
 * the red the rest of the site uses. Rather than paint the button with whatever
 * arrived, the intent is read off it and the site's own destructive style is
 * used, so every dangerous button looks the same.
 *
 * @param {string} [color]
 * @returns {boolean}
 */
function isDestructive(color) {
    const hex = String(color || '').trim().replace('#', '');
    if (hex.length !== 3 && hex.length !== 6) return false;

    const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
    const red = parseInt(full.slice(0, 2), 16);
    const green = parseInt(full.slice(2, 4), 16);
    const blue = parseInt(full.slice(4, 6), 16);
    if ([red, green, blue].some(Number.isNaN)) return false;

    return red > 120 && red > green * 1.6 && red > blue * 1.6;
}

/**
 * The result object, in the shape the call sites destructure.
 * @returns {Object}
 */
function resultFrom(outcome, fallbackValue) {
    const base = { isConfirmed: false, isDenied: false, isDismissed: false, value: undefined };

    if (outcome.action === 'confirm') {
        base.isConfirmed = true;
        base.value = outcome.value !== undefined ? outcome.value : fallbackValue;
        return base;
    }
    if (outcome.action === 'deny') {
        base.isDenied = true;
        base.value = false;
        return base;
    }

    base.isDismissed = true;
    base.dismiss = outcome.action === 'cancel' ? 'cancel' : (outcome.reason || 'cancel');
    return base;
}

/**
 * Open a dialog.
 *
 * @param {Object} options - the SweetAlert options this codebase uses
 * @returns {Promise<Object>} { isConfirmed, isDenied, isDismissed, value, dismiss }
 */
function fire(options) {
    const config = options || {};

    // A toast is not a dialog: nothing is blocked, nothing is answered.
    //
    // Twenty two call sites open a modal with a timer and no buttons, in eight
    // different durations, to say things like "Copied!". That is a toast that
    // took the page hostage for 1.2 seconds, so it becomes one. Nothing about
    // the call has to change, and the answer stays the shape it was.
    if (config.toast || selfClosing(config)) {
        // The timer the caller passed is deliberately dropped: see toast.js,
        // which decides how long something stays from what kind of thing it is.
        toast(toastMessage(config), config.icon);
        return Promise.resolve({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: 'timer' });
    }

    const body = document.createDocumentFragment();

    if (config.html) {
        body.appendChild(htmlNode(config.html));
    } else if (config.text) {
        body.appendChild(el('p', '', config.text));
    }

    const input = inputNode(config);
    if (input) body.appendChild(input);

    const field = input ? input.__field : null;
    const readValue = () => (field ? field.value : true);

    const controller = openDialog({
        title: config.title,
        icon: chip(config.icon),
        body,
        width: config.width,
        buttons: buttonsFor(config),
        focus: field,
        dismissable: function () {
            if (typeof config.allowOutsideClick === 'function') return Boolean(config.allowOutsideClick());
            return config.allowOutsideClick !== false;
        },
        // Kept apart from the backdrop, the way the plugin has it: a dialog that
        // refuses a stray click still lets a keyboard out.
        escapable: config.allowEscapeKey !== false,
        onAction: function (id) {
            if (id !== 'confirm' || typeof config.preConfirm !== 'function') {
                return id === 'confirm' ? readValue() : undefined;
            }

            if (config.showLoaderOnConfirm) controller.setLoading(true);

            return Promise.resolve(config.preConfirm(readValue())).then(function (value) {
                if (value === false) return false;
                // Undefined means the handler only looked; the input value is
                // still what the caller asked for.
                return value === undefined ? readValue() : value;
            }).catch(function (err) {
                controller.setValidation(err && err.message ? err.message : String(err));
                return false;
            });
        },
        onOpen: function (ctl) {
            current = ctl;
            if (typeof config.didOpen === 'function') config.didOpen(ctl.panel);
        }
    });

    if (config.timer) {
        setTimeout(function () { controller.close(null, { reason: 'timer' }); }, config.timer);
    }

    return controller.result.then(function (outcome) {
        if (current === controller) current = null;
        return resultFrom(outcome, readValue());
    });
}

/** Close whatever is open, as a dismissal. */
function close() {
    if (current) current.close(null, { reason: 'close' });
}

/** Buttons off, spinner on, for work that started without a preConfirm. */
function showLoading() {
    if (current) current.setLoading(true);
}

/** @returns {boolean} */
function isLoading() {
    return Boolean(current && current.isLoading());
}

/**
 * Put a message under the body and keep the dialog open.
 * @param {string} message
 */
function showValidationMessage(message) {
    if (current) {
        current.setLoading(false);
        current.setValidation(message);
    }
}

/** @returns {HTMLElement|null} */
function getConfirmButton() {
    return current ? current.button('confirm') : null;
}

/** @returns {HTMLElement|null} the panel, which is what didOpen hands out */
function getPopup() {
    return current ? current.panel : null;
}

module.exports = {
    fire,
    close,
    showLoading,
    isLoading,
    showValidationMessage,
    getConfirmButton,
    getPopup
};
